/* eslint-disable max-lines, max-classes-per-file, max-lines-per-function, class-methods-use-this */
import { redirectTo, deviceType } from "common/utils/browser";
import { logUploadErrorEvent } from "common/utils/mixpanel/dropzone-error-event-tracker";
import { isCustomizablePlp } from "components/jiffy/product-details/customizable-made-page-utils";
import { trackRudderstackImageUploadStarted } from "common/utils/rudderstack/tracker";
import { logUploadError } from "common/utils/app-monitoring/log-upload-error";
import { api } from "jiffy-api";
import DesignUploadCtaPlpCookieClass from "components/jiffy/design-upload-cta/design-upload-cta-plp/design-upload-cta-plp-cookie-class";
import { submitDtfEvent, submitCompletedEvent } from "common/utils/mixpanel/submit-dtf-events";
import ProductDetailsDtfProgress from "components/jiffy/product-details-dtf-sheet/product-details-dtf-progress/product-details-dtf-progress";
import { isFileTypeSupported, logUnsupportedFileTypeEvent } from "common/file-type-helper";
import { track } from "common/utils/mixpanel/tracker";
import { generateUUID } from "common/utils/uuid";
import { setUploadUuid } from "common/utils/transfer-upload/upload-uuid-tracker";
import { materializeFile } from "common/artwork-file-types";
import { getUploadTimeout } from "common/upload-constants";
import "./product-card-transfers.scss";

class ProductCardTransfer {
  constructor($container) {
    this.catalogNumber = $container.dataset.catalogNumber;
    this.printAsIs = $container.dataset.printAsIs === "true";
    this.businessLine = $container.dataset.businessLine;

    this.uploadType = null;
    this.uploadedFile = null;
    this.messageIndex = 0;
    this.percentage = 0;
    this.timeoutInterval = null;

    this.$container = $container;
    this.analyticsPageName = this.$container?.dataset?.pageName || "category";
    // TODO: Remove this when we have a proper way to get the composition
    this.$parentContainer = this.$container.closest(".js-product-card");
    this.isFlash = this.$container.dataset.flash === "true";
    this.isMade = this.$container.dataset.made === "true";
    this.composition = this.$parentContainer.dataset.composition;
    // Use Map to store uploadUuid per file to prevent race conditions
    this.uploadUuids = new Map();

    this.progressBar = new ProductDetailsDtfProgress(
      this.$container,
      this.progressReport.bind(this)
    );

    this.handleElementClick = this.handleElementClick.bind(this);
    this.handleFileInputChange = this.handleFileInputChange.bind(this);
    this.handleDragLeave = this.handleDragLeave.bind(this);
    this.handleDrop = this.handleDrop.bind(this);
    this.handleDragOver = this.handleDragOver.bind(this);
    this.handleDragEnter = this.handleDragEnter.bind(this);
    this.updateProgressText = this.updateProgressText.bind(this);
    this.resetUploadMessages = this.resetUploadMessages.bind(this);
  }

  init() {
    [this.$fileInput, this.$progressTextContainer, this.$errorContainer] = this.getDOMElements();

    this.$fileInput.addEventListener("change", this.handleFileInputChange);
    this.$container.addEventListener("click", this.handleElementClick);
    this.$container.addEventListener("dragover", this.handleDragOver);
    this.$container.addEventListener("dragenter", this.handleDragEnter);
  }

  getDOMElements() {
    return [
      this.$container.querySelector('input[type="file"]'),
      this.$container.querySelector(".js-product-card-progress-text"),
      this.$container.querySelector(".js-product-card-error")
    ];
  }

  handleFileInputChange(e) {
    e.preventDefault();
    const [file] = e.target.files;
    if (!file) return;

    const { valid, message } = isFileTypeSupported(file, this.printAsIs);
    e.target.value = "";

    if (!valid) {
      this.setErrorMessage(message);
      logUnsupportedFileTypeEvent(
        file,
        {
          page: "transfers",
          pdp_type: "",
          business_line: this.businessLine
        },
        message
      );
      return;
    }

    this.uploadedFile = file;
    this.uploadType = "browse";
    this.trackFileCount();
    this.uploadArtwork();
  }

  handleElementClick(e) {
    e.stopPropagation();
    this.trackAddFileAreaClick();
  }

  handleDragOver(e) {
    this.$container.classList.add("product-card-transfers--dropping");
    e.preventDefault();
  }

  handleDragEnter() {
    this.$container.classList.add("product-card-transfers--dropping");

    this.$container.addEventListener("dragleave", this.handleDragLeave);
    this.$container.addEventListener("drop", this.handleDrop);
  }

  handleDragLeave() {
    this.$container.classList.remove("product-card-transfers--dropping");

    this.$container.removeEventListener("dragleave", this.handleDragLeave);
    this.$container.removeEventListener("drop", this.handleDrop);
  }

  handleDrop(e) {
    e.preventDefault();
    if (!e.dataTransfer.files || !e.dataTransfer.files.length) return;

    [this.uploadedFile] = e.dataTransfer.files;
    if (!this.uploadedFile) return;

    const { valid, message } = isFileTypeSupported(this.uploadedFile, this.printAsIs);
    if (!valid) {
      this.setErrorMessage(message);
      logUnsupportedFileTypeEvent(
        this.uploadedFile,
        {
          page: "transfers",
          pdp_type: "",
          business_line: this.businessLine
        },
        message
      );
      return;
    }
    this.trackFileCount();
    this.uploadType = "drop";
    this.uploadArtwork();

    this.$container.removeEventListener("dragleave", this.handleDragLeave);
    this.$container.removeEventListener("drop", this.handleDrop);
  }

  trackFileCount(length = 1) {
    submitDtfEvent("image_upload_count", {
      page: this.analyticsPageName,
      catalog_number: this.catalogNumber,
      business_line: this.businessLine,
      image_count: length
    });
  }

  trackAddFileAreaClick() {
    submitDtfEvent("transfer_choose_file_clicked", {
      page: this.analyticsPageName,
      catalog_number: this.catalogNumber,
      business_line: this.businessLine
    });
  }

  trackUploadStarted() {
    const uploadUuid = generateUUID();
    // Store uploadUuid per file to prevent race conditions
    this.uploadUuids.set(this.uploadedFile.name, uploadUuid);
    // MN-4800: persist via the WeakMap-backed tracker so logUploadErrorEvent's
    // getErrorContext picks up the same uploadUuid into both legacy and
    // transfer_upload_* events.
    setUploadUuid(this.uploadedFile, uploadUuid);

    submitDtfEvent("image_upload_started", {
      page: this.analyticsPageName,
      catalog_number: this.catalogNumber,
      type: this.uploadType,
      file_name: this.uploadedFile.name,
      file_type: this.uploadedFile.type,
      file_size: this.uploadedFile.size,
      dpi: 300,
      project_id: null,
      upload_uuid: uploadUuid
    });
    trackRudderstackImageUploadStarted({
      catalog_number: this.catalogNumber,
      dpi: 300,
      file_name: this.uploadedFile.name,
      file_type: this.uploadedFile.type,
      file_size: this.uploadedFile.size,
      location: this.analyticsPageName,
      business_line: this.businessLine,
      artworks_count: 1,
      upload_uuid: uploadUuid
    });
  }

  sendUploadCompletedEventOnError(e, uploadStartTime) {
    // e is an Axios object. Timeouts (ECONNABORTED) and network errors have no
    // `e.response`, so guard the access — otherwise this throws before the catch
    // can run processError/logUploadErrorEvent and the UI cleanup, leaving the
    // card stuck "Uploading…" and the failure unreported (silent: true removed
    // the axios auto-notify backstop).
    const errorMessage = e?.response?.data?.error || e?.message || "Upload error";
    const artwork = {
      filetype: this.uploadedFile.type,
      filesize: this.uploadedFile.size,
      errors: [errorMessage]
    };

    this.trackUploadCompleted(artwork, uploadStartTime, false);
  }

  trackUploadCompleted(artwork, uploadStartTime, success = true) {
    if (!artwork) return;

    // Get the uploadUuid for this specific file to prevent race conditions
    const uploadUuid = this.uploadUuids.get(this.uploadedFile.name);

    submitCompletedEvent({
      file: this.uploadedFile,
      catalogNumber: this.catalogNumber,
      uploadedOn: this.analyticsPageName,
      multiArtwork: false,
      businessLine: this.businessLine,
      dpi: 300,
      artworksCount: 1,
      validationErrors: artwork.errors,
      uploadStartTime,
      artwork,
      uploadUuid,
      success
    });

    // Clean up the uploadUuid for this file
    this.uploadUuids.delete(this.uploadedFile.name);
  }

  startProgressBar() {
    this.progressBar.setFilename(this.uploadedFile.name);
    this.progressBar.start();
  }

  setProgressBarValue(progress) {
    this.progressBar.setValues(progress);
  }

  stopProgressBar() {
    this.progressBar.stop();
  }

  hideProgressBar() {
    this.progressBar.hide();
  }

  updateProgressText(text) {
    this.$progressTextContainer.textContent = text;
  }

  setErrorMessage(text) {
    this.$errorContainer.textContent = text;
  }

  processError(e) {
    const errorResponse = e.response?.data;
    const handledError = ["backend_file_validation", "backend_processing_error"].includes(
      errorResponse?.error_type
    );
    const errorMessage = errorResponse?.error || e.message;
    let uploadError;

    if (errorMessage === "Network Error") {
      uploadError = this.percentage === 100 ? "Processing error" : "Upload error";
    }

    logUploadError({
      error_message: errorMessage,
      error: e.message,
      handledError,
      uploadError,
      errorResponse,
      status: e.response?.status
    });

    const pageInfo = {
      pageValue: this.analyticsPageName,
      businessLine: this.businessLine
    };
    logUploadErrorEvent(pageInfo, this.uploadedFile, e);

    this.$container.classList.remove("product-card-transfers--uploading");

    this.$fileInput.value = "";
    this.updateProgressText("");
    this.setErrorMessage(uploadError || errorMessage);
    this.resetUploadMessages();
  }

  progressReport(percentage) {
    this.percentage = percentage;

    if (percentage === 100 && !this.timeoutInterval) {
      this.updateProgressText("Uploading file");
    }
  }

  startUploadUI() {
    this.$container.classList.add("product-card-transfers--uploading");
    this.$container.classList.remove("product-card-transfers--dropping");
    this.startProgressBar();
  }

  toggleOtherDropzones(toEnabled) {
    const $otherContainers = document.querySelectorAll(
      `.js-product-card-transfers:not(#${this.$container.id})`
    );

    $otherContainers.forEach($container => {
      $container.classList.toggle("product-card-transfers--unavailable", !toEnabled);
      const fileInput = $container.querySelector('input[type="file"]');
      fileInput.disabled = !toEnabled;
    });
  }

  async uploadArtwork() {
    this.startUploadUI();
    this.toggleOtherDropzones(false);
    ProductCardTransfer.resetAllErrorMessages();
    ProductCardTransfer.toggleProductLinks(false);
    this.percentage = 0;

    // Show preparing status for files from external sources (Google Drive, Dropbox)
    this.updateProgressText("Preparing file...");

    // Materialize the file to handle cloud storage providers (Google Drive, Dropbox) on mobile
    const { file: materializedFile, error: materializeError } = await materializeFile(
      this.uploadedFile,
      {
        timeoutMs: 120000, // 2 minute timeout for large files from cloud storage
        onProgress: progress => {
          // Show preparation progress (0-10% range)
          this.setProgressBarValue(progress * 10);
        }
      }
    );

    if (materializeError || !materializedFile) {
      this.setErrorMessage(materializeError || "Failed to read file. Please try again.");
      this.$container.classList.remove("product-card-transfers--uploading");
      this.stopProgressBar();
      ProductCardTransfer.toggleProductLinks(true);
      this.toggleOtherDropzones(true);
      return;
    }

    // Use materialized file for upload
    this.uploadedFile = materializedFile;

    const formData = new FormData();
    formData.append("upload_source", `${this.catalogNumber}_PLP`);
    formData.append("generate_link", true);
    formData.append("file", this.uploadedFile);
    formData.append("catalog_number", this.catalogNumber);
    formData.append("is_made", this.isMade);
    formData.append("is_made_flash", this.isFlash);
    if (isCustomizablePlp()) formData.append("made_dedicated_pdp", true);
    formData.append("ac", this.composition);

    const uploadStartTime = performance.now();

    try {
      this.trackUploadStarted();
      this.updateProgressText("Uploading...");
      // `silent: true` suppresses the axios layer's auto-notify to Honeybadger.
      // Validation rejections here (oversized, sub-200px, malformed gang sheet,
      // etc.) are expected 422s, not JS bugs — processError already reports them
      // via logUploadErrorEvent. The shared uploadArtworkWithTracking helper is
      // not used because it logs a synthetic file rebuilt from FormData, losing
      // the WeakMap-backed upload_uuid set on the original File by setUploadUuid.
      const response = await api.dtf.uploadArtwork(formData, {
        raw: true,
        headers: { "Content-Type": undefined },
        useAxiosForRequest: true,
        silent: true,
        timeout: getUploadTimeout(this.uploadedFile?.size),
        onUploadProgress: ({ progress, loaded, total }) => {
          // Calculate upload progress (10-100% range)
          const uploadProgress = 10 + (progress || loaded / total || 0) * 90;
          this.setProgressBarValue(uploadProgress);
        }
      });

      this.trackUploadCompleted(response, uploadStartTime);
      this.resetUploadMessages();
      this.hideProgressBar();

      this.updateProgressText("Redirecting...");
      this.redirect(response);
    } catch (e) {
      this.sendUploadCompletedEventOnError(e, uploadStartTime);
      this.processError(e);
      this.stopProgressBar();
      ProductCardTransfer.toggleProductLinks(true);
      this.toggleOtherDropzones(true);
    }
  }

  resetUploadMessages() {
    this.updateProgressText("");
    if (this.timeoutInterval) {
      clearTimeout(this.timeoutInterval);
      this.timeoutInterval = null;
    }
  }

  resetAllStates() {
    this.$container.classList.remove("product-card-transfers--uploading");
    this.hideProgressBar();
    this.updateProgressText("");
    this.$fileInput.value = "";
  }

  redirect(response) {
    // BAT-770: Neck Label PLP uploads redirect to /design-tag. The server also
    // sets `upload_design_on_print_plp` as a belt-and-suspenders write, but
    // CloudFront strips `Set-Cookie` from JSON API responses, so we mirror the
    // write client-side. Uses `response.plp_cookie_url` (a short imgix URL) —
    // not `response.url`, which is a 2KB+ presigned S3 URL that pushes the
    // base64-JSON cookie past the browser's ~4KB per-cookie limit.
    if (response.redirect_url === "/design-tag" && response.id && response.plp_cookie_url) {
      DesignUploadCtaPlpCookieClass.cookie = {
        id: response.id,
        url: response.plp_cookie_url,
        original_image_url: response.plp_cookie_url,
        original_mime: response.filetype,
        updated_at: new Date().toISOString()
      };
    }

    redirectTo(response.redirect_url);
  }

  static resetAllErrorMessages() {
    document.querySelectorAll(".js-product-card-error").forEach($error => {
      // eslint-disable-next-line no-param-reassign
      $error.textContent = "";
    });
  }

  static toggleProductLinks(enable) {
    const $productCards = document.querySelectorAll(".product-card");
    $productCards.forEach($productCard => {
      $productCard.classList.toggle("product-card--disabled", !enable);
    });
  }
}

class MadeProductCardTransfer extends ProductCardTransfer {
  init() {
    super.init();

    this.handleCardClick = this.handleCardClick.bind(this);
    this.logEvent = this.logEvent.bind(this);

    this.$card = this.$container.closest(".js-product-card");
    this.$card.addEventListener("click", this.handleCardClick);
  }

  handleCardClick() {
    this.logEvent("jiffyMade_PLP_ProductCard_Click");
  }

  redirect(response) {
    this.logEvent("jiffyMade_PLP_StartDesign_Click");
    redirectTo(response.redirect_url);
  }

  logEvent(name) {
    track(name, {
      product_id: this.catalogNumber,
      page_url: window.location.href,
      device_type: deviceType()
    });
  }
}

export default () => {
  const $rootElements = document.querySelectorAll(".js-product-card-transfers");
  const productCardTransfers = [];
  $rootElements.forEach($element => {
    const isMade = $element.dataset.made === "true";
    const productCardTransfer = (() => {
      if (isMade) {
        return new MadeProductCardTransfer($element);
      }

      return new ProductCardTransfer($element);
    })();
    productCardTransfer.init();
    productCardTransfers.push(productCardTransfer);
  });

  // workaround for safari's bfcache
  window.addEventListener("pageshow", e => {
    if (!e.persisted) return;

    ProductCardTransfer.toggleProductLinks(true);
    productCardTransfers.forEach(productCardTransfer => {
      productCardTransfer.resetAllStates();
    });
  });
};
